import { ICarouselProps } from "@coherence-design-system/controls";
import { OverviewAndSummaryMutationProps, ProductViewFiltersProps, ProductViewSummaryTabCardProps } from "../Common/ProductView.interfaces";
import { DetailedViewTab } from "../Containers/FeatureTabs/DetailedViewTab";
import { SegmentationTab } from "../Containers/FeatureTabs/SegmentationTab";
import { SummaryTab as FeatureViewSummaryTab } from "../Containers/FeatureTabs/SummaryTab";
import { CohortsTab } from "../Containers/ProductViewTabs/CohortsTab";
import { CustomerEngagementsForProductTab } from "../Containers/ProductViewTabs/CustomerEngagementsForProductTab";
import { FeatureTab } from "../Containers/ProductViewTabs/FeatureTab";
import { KeyMetricsFunnelView } from "../Containers/ProductViewTabs/KeyMetricsFunnelView";
import { OpportunityTenantsTab } from "../Containers/ProductViewTabs/OpportunityTenantsTab";
import { ProductTelemetryTab, tenantCountMetricsTooltipMessage } from "../Containers/ProductTelemetryTab/ProductTelemetryTab";
import { SupportTab as ProductViewSupportTab } from "../Containers/ProductViewTabs/SupportTab";
import TopCustomers from "../Containers/ProductViewTabs/TopCustomersTab";
import { FeatureRequestTab } from "../Containers/ProductViewTabs/FeatureRequestTab";
import OpenCasesTab from "../Containers/SupportTabs/OpenCasesTab";
import { VolumeTab } from "../Containers/SupportTabs/VolumeTab";
import { QualityTab } from "../Containers/SupportTabs/QualityTab";
import { COSOCDataTab } from "../Containers/ProductViewTabs/COSOCDataTab";
import ConsumptionRevenueTab from "../Containers/ConsumptionRevenue/ConsumptionRevenueTab";
import PartnerInfluence from "../Containers/PartnerInfluence/partnerInfluence";

export const PRODUCT_VIEW_DATA = {
  PRODUCT_VIEW: {
    INITIAL_TAB_NAME: "industryname",
    INITIAL_MUTATION: {
      data: { overviewData: [], summaryData: [] }
    } as OverviewAndSummaryMutationProps,
    INITIAL_PRODUCT_VIEW_FILTERS: {
      hasPartner: "",
      industry: "",
      isCAT: "",
      isFastTrackStrategic: "",
      isSST: "",
      productName: "",
      productFamily: "",
      region: "",
      segment: "",
      subSegment: ""
    } as ProductViewFiltersProps,
    DEFAULT_PRODUCT_NAME: "Microsoft Intune",
    DEFAULT_PRODUCT_ID: "33",
    CARD_N0_DATA_MESSAGE: "No data is available. Try resetting filters",
    VIEW_NO_DATA_MESSAGE: "This view has no data to display",
    VIEWED_KEY: "recentlyViewedProducts",
    RECENTLY_VIEWED_LIMIT: 5
  },
  TABS: {
    DATA: {
      pivot: { ariaLabel: "Pivot Selection" },
      pivotItems: [
        { headerText: "Product Telemetry", itemKey: "ProductTelemetryTab", component: ProductTelemetryTab },
        { headerText: "Features Telemetry", itemKey: "FeatureProductView", component: FeatureTab },
        { headerText: "Consumption Revenue", itemKey: "ConsumptionRevenueTab", component: ConsumptionRevenueTab },
        { headerText: "Cohorts", itemKey: "CohortsTab", component: CohortsTab },
        { headerText: "Opportunity Tenants", itemKey: "OpportunityTenantsTab", component: OpportunityTenantsTab },
        { headerText: "Funnel View", itemKey: "KeyMetricsFunnelView", component: KeyMetricsFunnelView },
        { headerText: "Customer Engagements", itemKey: "CustomerEngagementsForProduct", component: CustomerEngagementsForProductTab },
        { headerText: "Feature Request", itemKey: "UnifiedInsights", component: FeatureRequestTab },
        { headerText: "Support", itemKey: "SupportTab", component: ProductViewSupportTab },
        { headerText: "Top Customers", itemKey: "TopCustomersTab", component: TopCustomers },
        { headerText: "Community", itemKey: "Community", component: COSOCDataTab },
        { headerText: "Partner Influence", itemKey: "PartnerInfluenceTab", component: PartnerInfluence },
      ]
    },
    TAB: {
      SUMMARY: {
        DATA: [
          {
            key: "product-view-summary-tab-key-metrics",
            header: {
              title: "How are usage and licenses trending?",
              tooltip:
                "See how important metrics are trending over time for security products. The graph below shows 6 month trend."
            },
            isTenantMetrics: 0
          },
          {
            key: "product-view-summary-tab-tenant-count-metric",
            header: {
              title: "How are tenants with usage and licenses trending?",
              tooltip: tenantCountMetricsTooltipMessage
            },
            isTenantMetrics: 1
          }
        ] as ProductViewSummaryTabCardProps[]
      },
      COHORTS: {
        CARD_HEADER_TOOLTIPS: {
          METRICS_DETAILS:
            "See which cohorts are growing in the grid below. Current shows current month value. MoM shows month on month change. You can sort based on current month numbers or MoM growth.",
          METRICS_BY_CATEGORY:
            "See top cohorts by a specific metric. Use the up and down buttons on right-bottom corner to navigate to different pages."
        }
      },
      OPPORTUNITY_TENANTS: {
        PAGE_SIZE_OPTIONS: [10, 25, 50, 75, 100],
        PAGINATION_MODEL: {
          page: 0,
          pageSize: 10
        },
        CARD_HEADER: {
          TOP_GROWING: {
            key: "product-view-opportunity-tenants-tab-growing-metrics",
            title: "",
            tooltip:
              "The grid below shows top growing tenants by comparing usage with previous month. Click on the the name hyperlink in the table to navigate to customer view."
          },
          TOP_DECLINING: {
            key: "product-view-opportunity-tenants-tab-declining-metrics",
            title: "",
            tooltip:
              "The grid below shows top declining tenants by comparing usage with previous month. Click on the the name hyperlink in the table to navigate to customer view."
          }
        }
      },
      KEY_METRICS_FUNNEL_VIEW: {
        CARD_HEADER: {
          key: "product-view-funnel-view-tab-funnel-key-metrics",
          title: "Key Metrics Funnel",
          tooltip: "Key metrics being shown in a funnel view in the chart below. These are current month values."
        }
      },
      TENANTS_METRICS_FUNNEL_VIEW: {
        CARD_HEADER: {
          key: "product-view-funnel-view-tab-funnel-tenants-count-metrics",
          title: "Tenants Count Metrics Funnel",
          tooltip: "Tenants Count metrics being shown in a funnel view in the chart below. These are current month values."
        }
      },
      FEATURE_VIEW: {
        TABS_DATA: {
          pivot: { ariaLabel: "product-view-feature-tab-aria-label" },
          pivotItems: [
            {
              headerText: "Summary",
              itemKey: "FeatureTabSummary",
              component: FeatureViewSummaryTab
            },
            { headerText: "Detailed View", itemKey: "FeatureTabDetailedView", component: DetailedViewTab },
            { headerText: "Segmentation", itemKey: "FeatureTabCohorts", component: SegmentationTab }
          ]
        },
        SUMMARY_TAB: {
          FEATURE_VIEW_ALL_UP: "Feature View - All Up",
          FEATURE_VIEW_MPU_ALL_UP: "Feature View - MPU All Up",
          FEATURE_VIEW_MDPU_ALL_UP: "Feature View - MDPU All Up",
        },
        DETAILED_VIEW: {
          INITIAL_CAROUSEL_DATA: {
            items: [],
            title: "",
            responsive: true,
            itemWidth: 298,
            breakpoints: {
              xxxl: { width: 1805, items: 4 },
              xxl: { width: 1366, items: 3 },
              xl: { width: 1024, items: 3 },
              l: { width: 640, items: 2 },
              m: { width: 480, items: 2 },
              s: { width: 320, items: 1 }
            },
            styles: {
              container: {
                "& > div": {
                  marginBottom: 5
                },
                "& > div:nth-child(2)": {
                  marginBottom: 0
                }
              }
            }
          } as ICarouselProps
        },
        FEATURE_VIEW_COHORTS_TAB: {}
      },
      SUPPORT_VIEW: {
        TABS_DATA: {
          pivot: { ariaLabel: "product-view-support-tab-aria-label" },
          pivotItems: [
            {
              headerText: "Volume",
              itemKey: "SupportVolumeSummary",
              component: VolumeTab
             },
             { headerText: "Quality", itemKey: "SupportCSATSummary", component: QualityTab },
             { headerText: "Open Cases", itemKey: "OpenCasesSummary", component: OpenCasesTab }
          ]
        },
        VOLUME_TAB: {
          SR_CRI_CLOSED_VOLUME:{title: "SR/CRI Volume Trend",tooltip:"This represents the volume distribution of Service Requests (SR) and Customer Reported Incidents (CRIs) by creation date.​"},
          TOP_TENANTS_SR_VOLUME: {title:"Top 10 Tenants By SR Volume",tooltip:"This represents the tenant distribution based on the volume of Service Requests.​"},
          TOP_COUNTRIES_SR_VOLUME: {title:"Top 10 Countries By SR Volume",tooltip:"This represents the distribution of support request volume by the country from which it was filed.​"},
          REQUEST_STATE:{title:"Service Request State",tooltip:"This indicates the current state of the Service Request, i.e., open or closed.​"},
          TOP_TENANTS_CRI_VOLUME: {title:"Top 10 Tenants By CRI Volume",tooltip:"This represents the tenant distribution based on the volume of Customer Reported Incidents.​"},
          TOP_COUNTRIES_CRI_VOLUME: {title:"Top 10 Countries By CRI Volume",tooltip:"This represents the volume distribution of Customer Reported Incidents based on the country where the relevant Service Request was filed.​"}
        },
        SUMMARY_TAB: {
          CSAT_BY_SR_VOLUME: {title: "CSAT Trend By SR Volume",tooltip:"This represents the volume distribution of Service Requests based on the Customer Satisfaction Score.​"},
          CSAT_BY_AGE_BUCKET:{title: "CSAT By SR Age Bucket",tooltip:"This represents the distribution of Service Requests by Customer Satisfaction Score and Case Age.​"},
          CSAT_BY_CRI_VOLUME: {title:"CSAT Trend By CRI Volume",tooltip:"This represents the volume distribution of Customer Reported Incidents based on the Customer Satisfaction Score.​"},
          SR_VOLUME_STAFFING_TYPE:{title:"SR Volume Distribution By Staffing Type",tooltip:"This represents the volume distribution of Service Requests based on the assigned staffing type (e.g., FTE, Delivery Partner, etc.).​"},
          SR_VOLUME_REOPEN_COUNT:{title:"SR Volume Distribution By Reopen Count",tooltip:"This represents the volume distribution of Service Requests based on the number of times a request was reopened on the same tenant.​"},
          SR_VOLUME_TRANSFER_COUNT:{title:"SR Volume Distribution By Transfer Count",tooltip:"This represents the volume distribution of Service Requests based on the number of times a request was transferred before reaching the correct team.​"}
        }
      }
    }
  },
};
